import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";

// Pages
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./Dashboard";
import PaymentPage from "./pages/PaymentPage";
import PricingPage from "./pages/PricingPage";
import LandingPage from "./pages/LandingPage";
import ContactPage from "./pages/ContactPage";

// Blog pages
import Blog from "./pages/Blog";
import HowToTrackSubscriptions from "./pages/blog/HowToTrackSubscriptions";
import BestExpenseTrackerForStudents from "./pages/blog/BestExpenseTrackerForStudents";
import StopPayingForForgottenSubscriptions from "./pages/blog/StopPayingForForgottenSubscriptions";
import AnalyzeBankStatementsAutomatically from "./pages/blog/AnalyzeBankStatementsAutomatically";
import TrackExpensesFromPDF from "./pages/blog/TrackExpensesFromPDF";
import HowToMaximizeCashback from "./pages/blog/HowToMaximizeCashback";
import WhyAlwaysBroke from "./pages/blog/WhyAlwaysBroke";
import BestExpenseTrackerWithBankUpload from "./pages/blog/BestExpenseTrackerWithBankUpload";
import AutoCategorizeExpensesAI from "./pages/blog/AutoCategorizeExpensesAI";
import TrackMultipleAccounts from "./pages/blog/TrackMultipleAccounts";
import BuildMonthlyBudget from "./pages/blog/BuildMonthlyBudget";
import FindHiddenSubscriptions from "./pages/blog/FindHiddenSubscriptions";


// Hooks & utils
import useRouteTheme from "./hooks/useRouteTheme";
import RouteThemeGate from "./components/RouteThemeGate";
import { enableAnalytics } from "./utils/analytics";

// Layout / helpers
import PrivacyPolicy from "./components/layout/PrivacyPolicy";
import TermsOfService from "./components/layout/TermsOfService";
import DataSecurity from "./components/layout/DataSecurity";
import ScrollToTop from "./components/layout/ScrollToTop";
import TrackPageView from "./components/TrackPageView";

// Providers
import ThemeProvider from "./components/providers/ThemeProvider";

// Navbar
import NavbarGate from "./components/layout/NavbarGate";

function App() {
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  // Sync auth across tabs
  useEffect(() => {
    const syncAuth = () => {
      setToken(localStorage.getItem("token"));
    };

    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, []);

  // Enable analytics only after consent
  useEffect(() => {
    const maybeEnableGA = () => {
      if (localStorage.getItem("cookieConsent") === "accepted") {
        enableAnalytics();
      }
    };

    maybeEnableGA();
    window.addEventListener("cookie-consent-updated", maybeEnableGA);

    return () =>
      window.removeEventListener("cookie-consent-updated", maybeEnableGA);
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          {/* Global helpers */}
          <RouteThemeGate />
          <ScrollToTop />
          <TrackPageView />

          {/* Navbar */}
          <NavbarGate />

          <Routes>
            {/* 🌐 Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/help" element={<ContactPage />} />

            {/* 📝 Blog */}
            <Route path="/blog" element={<Blog />} />
            <Route
              path="/blog/how-to-track-subscriptions"
              element={<HowToTrackSubscriptions />}
            />
            <Route
              path="/blog/best-expense-tracker-for-students"
              element={<BestExpenseTrackerForStudents />}
            />

            <Route
  path="/blog/stop-paying-for-forgotten-subscriptions"
  element={<StopPayingForForgottenSubscriptions />}
/>
<Route
  path="/blog/analyze-bank-statements-automatically"
  element={<AnalyzeBankStatementsAutomatically />}
/>

<Route
  path="/blog/track-expenses-from-bank-statement-pdf"
  element={<TrackExpensesFromPDF />}
/>

<Route
  path="/blog/maximize-credit-card-cashback"
  element={<HowToMaximizeCashback />}
/>

<Route
  path="/blog/why-you-are-always-broke"
  element={<WhyAlwaysBroke />}
/>

<Route
  path="/blog/best-expense-tracker-bank-statement-upload"
  element={<BestExpenseTrackerWithBankUpload />}
/>

<Route
  path="/blog/ai-expense-categorization"
  element={<AutoCategorizeExpensesAI />}
/>

<Route
  path="/blog/track-expenses-multiple-accounts"
  element={<TrackMultipleAccounts />}
/>

<Route
  path="/blog/monthly-budget-guide"
  element={<BuildMonthlyBudget />}
/>

<Route
  path="/blog/find-hidden-subscriptions"
  element={<FindHiddenSubscriptions />}
/>


            {/* 🔐 Auth */}
            <Route
              path="/login"
              element={token ? <Navigate to="/dashboard" /> : <Login />}
            />
            <Route
              path="/signup"
              element={token ? <Navigate to="/dashboard" /> : <Signup />}
            />

            {/* 💳 Payment */}
            <Route path="/payment" element={<PaymentPage />} />

            {/* 📊 App */}
            <Route
              path="/dashboard/*"
              element={token ? <Dashboard /> : <Navigate to="/login" />}
            />

            {/* 📜 Legal */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/data-security" element={<DataSecurity />} />

            {/* ❓ Fallback */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
